import {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery
} from "@tanstack/react-query";
import { ErrorType, fetcher } from "./fetcher";
import { useUrls } from "../utils/url";
import { getUserToken } from "../utils/helpers/user";

export type UserInfoData = {
  attributes: {
    cpr: number;
    userId: string;
    uniqueId: `${string}-${string}-${string}-${string}-${string}`;
    agencies: {
      agencyId: `${number}`;
      userId: string;
      userIdType: string;
    }[];
    municipality: `${number}`;
    municipalityAgencyId: `${number}`;
    pincode: string;
  };
};

const getUserInfoQueryKey = (url: string): QueryKey => {
  const userToken = getUserToken();

  if (!userToken) {
    // eslint-disable-next-line no-console
    console.error("userToken is missing");
  }

  // The token is part of the key so a new login does not reuse the previous
  // user's cached info.
  return userToken ? [url, userToken] : [url];
};

type UserInfoFunction = () => Promise<UserInfoData | null | undefined>;

const useUserInfo = <
  TData = Awaited<ReturnType<UserInfoFunction>>,
  TError = ErrorType<void>
>(
  queryOptions?: Partial<
    UseQueryOptions<Awaited<ReturnType<UserInfoFunction>>, TError, TData>
  >
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const u = useUrls();
  const userinfoUrl = u("userinfoUrl");

  const url = String(userinfoUrl);
  const queryKey = queryOptions?.queryKey ?? getUserInfoQueryKey(url);

  const getUserInfo = (infoUrl: string, signal?: AbortSignal) => {
    return fetcher<UserInfoData>({
      url: infoUrl,
      method: "get",
      signal
    });
  };

  const queryFn: QueryFunction<Awaited<ReturnType<UserInfoFunction>>> = () =>
    getUserInfo(url);

  const query = useQuery<Awaited<ReturnType<UserInfoFunction>>, TError, TData>({
    queryKey,
    queryFn,
    ...queryOptions
  });

  return {
    queryKey,
    ...query
  };
};

export default useUserInfo;
